import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import ClientLayout from '../../components/layouts/ClientLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Camera, Image, Loader2, Upload, Heart, X, ChevronLeft, ChevronRight,
  Images, Download, User
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AttendeeGallery = () => {
  const { user, token } = useAuth();
  const [albums, setAlbums] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [photoUrls, setPhotoUrls] = useState({});
  
  const fileInputRef = useRef(null);
  const eventId = user?.event_id;

  useEffect(() => {
    if (eventId) {
      fetchAlbums();
      fetchPhotos();
    }
  }, [eventId, selectedAlbum]);

  const fetchAlbums = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gallery/albums?event_id=${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlbums(response.data);
    } catch (error) {
      console.error('Failed to fetch albums:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPhotos = async () => {
    try {
      const url = selectedAlbum 
        ? `${API_URL}/api/gallery/photos?event_id=${eventId}&album_id=${selectedAlbum.id}`
        : `${API_URL}/api/gallery/photos?event_id=${eventId}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPhotos(response.data.photos || []);
      
      // Load photo URLs
      for (const photo of response.data.photos || []) {
        loadPhotoUrl(photo);
      }
    } catch (error) {
      console.error('Failed to fetch photos:', error);
    }
  };

  const loadPhotoUrl = async (photo) => {
    if (photoUrls[photo.id]) return;
    
    try {
      const response = await axios.get(
        `${API_URL}/api/gallery/photos/${photo.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      const blobUrl = URL.createObjectURL(response.data);
      setPhotoUrls(prev => ({ ...prev, [photo.id]: blobUrl }));
    } catch (error) {
      console.error(`Failed to load photo ${photo.id}:`, error);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    if (files.length > 10) {
      toast.error('Maximum 10 photos per upload');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('event_id', eventId);
      if (selectedAlbum) {
        formData.append('album_id', selectedAlbum.id);
      }
      files.forEach(file => formData.append('files', file));
      
      const response = await axios.post(
        `${API_URL}/api/gallery/photos/bulk-upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        }
      );
      
      const { total_uploaded, total_failed } = response.data;
      
      if (total_uploaded > 0) {
        toast.success(`${total_uploaded} photo(s) uploaded successfully`);
      }
      if (total_failed > 0) {
        toast.error(`${total_failed} photo(s) failed to upload`);
      }
      
      fetchPhotos();
      fetchAlbums();
    } catch (error) {
      toast.error('Failed to upload photos');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleLikePhoto = async (photoId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/gallery/photos/${photoId}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPhotos(prev => prev.map(p => 
        p.id === photoId ? { ...p, like_count: response.data.like_count } : p
      ));
      
      toast.success(response.data.action === 'liked' ? 'Photo liked!' : 'Like removed');
    } catch (error) {
      toast.error('Failed to like photo');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API_URL}/api/gallery/photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Photo deleted');
      
      // Revoke blob URL
      if (photoUrls[photoId]) {
        URL.revokeObjectURL(photoUrls[photoId]);
        setPhotoUrls(prev => {
          const newUrls = { ...prev };
          delete newUrls[photoId];
          return newUrls;
        });
      }
      
      fetchPhotos();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const navigateLightbox = (direction) => {
    const newIndex = lightboxIndex + direction;
    if (newIndex >= 0 && newIndex < photos.length) {
      setLightboxIndex(newIndex);
    }
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-maroon" />
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="space-y-6" data-testid="attendee-gallery">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Camera className="h-6 w-6 text-maroon" />
              Photo Gallery
            </h1>
            <p className="text-gray-500">Browse and share reunion memories</p>
          </div>
          
          <Button 
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-photos-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading {uploadProgress}%
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Share Photos
              </>
            )}
          </Button>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            data-testid="photo-file-input"
          />
        </div>

        {/* Albums */}
        {albums.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Images className="h-5 w-5" />
                Albums
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 overflow-x-auto pb-2">
                {/* All Photos */}
                <div 
                  className={`flex-shrink-0 w-32 cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                    !selectedAlbum ? 'border-maroon ring-2 ring-maroon/20' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedAlbum(null)}
                >
                  <div className="aspect-square bg-gradient-to-br from-maroon/20 to-gold/20 flex items-center justify-center">
                    <Images className="h-8 w-8 text-maroon" />
                  </div>
                  <div className="p-2 bg-white">
                    <p className="font-medium text-xs truncate">All Photos</p>
                  </div>
                </div>

                {albums.map(album => (
                  <div 
                    key={album.id}
                    className={`flex-shrink-0 w-32 cursor-pointer rounded-xl overflow-hidden border-2 transition-all ${
                      selectedAlbum?.id === album.id ? 'border-maroon ring-2 ring-maroon/20' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedAlbum(album)}
                  >
                    <div className="aspect-square bg-gray-100 flex items-center justify-center">
                      {album.cover_photo && photoUrls[album.cover_photo.id] ? (
                        <img 
                          src={photoUrls[album.cover_photo.id]} 
                          alt={album.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Image className="h-8 w-8 text-gray-400" />
                      )}
                    </div>
                    <div className="p-2 bg-white">
                      <p className="font-medium text-xs truncate">{album.name}</p>
                      <p className="text-xs text-gray-500">{album.photo_count}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Photos Grid */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              {selectedAlbum ? selectedAlbum.name : 'All Photos'}
            </CardTitle>
            <CardDescription>
              {photos.length} photos
            </CardDescription>
          </CardHeader>
          <CardContent>
            {photos.length === 0 ? (
              <div className="text-center py-12">
                <Camera className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No photos yet</p>
                <p className="text-gray-400 text-sm mt-2">Be the first to share a memory!</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Photos
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {photos.map((photo, index) => (
                  <div 
                    key={photo.id}
                    className="relative group aspect-square rounded-lg overflow-hidden cursor-pointer bg-gray-100"
                    onClick={() => openLightbox(index)}
                    data-testid={`photo-${photo.id}`}
                  >
                    {photoUrls[photo.id] ? (
                      <img 
                        src={photoUrls[photo.id]} 
                        alt={photo.caption || 'Photo'}
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                      </div>
                    )}
                    
                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all">
                      <div className="absolute bottom-0 left-0 right-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t from-black/60 to-transparent">
                        <div className="flex items-center justify-between text-white text-xs">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {photo.uploader_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Heart className="h-3 w-3" />
                            {photo.like_count || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Lightbox */}
        {lightboxOpen && photos[lightboxIndex] && (
          <div 
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={() => setLightboxOpen(false)}
          >
            <Button 
              size="icon" 
              variant="ghost" 
              className="absolute top-4 right-4 text-white hover:bg-white/10"
              onClick={() => setLightboxOpen(false)}
            >
              <X className="h-6 w-6" />
            </Button>
            
            <Button 
              size="icon" 
              variant="ghost" 
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10"
              onClick={(e) => { e.stopPropagation(); navigateLightbox(-1); }}
              disabled={lightboxIndex === 0}
            >
              <ChevronLeft className="h-8 w-8" />
            </Button>
            
            <div className="max-w-4xl max-h-[80vh] px-16" onClick={e => e.stopPropagation()}>
              {photoUrls[photos[lightboxIndex].id] && (
                <img 
                  src={photoUrls[photos[lightboxIndex].id]}
                  alt={photos[lightboxIndex].caption || 'Photo'}
                  className="max-w-full max-h-[70vh] object-contain mx-auto"
                />
              )}
              
              <div className="mt-4 text-center">
                <p className="text-white/80 text-sm">
                  Uploaded by {photos[lightboxIndex].uploader_name}
                  {photos[lightboxIndex].uploader_role === 'organizer' && (
                    <Badge className="ml-2 bg-maroon">Organizer</Badge>
                  )}
                </p>
                {photos[lightboxIndex].caption && (
                  <p className="text-white mt-2">{photos[lightboxIndex].caption}</p>
                )}
              </div>
              
              <div className="flex items-center justify-center gap-4 mt-4">
                <Button 
                  variant="ghost" 
                  className="text-white hover:bg-white/10"
                  onClick={() => handleLikePhoto(photos[lightboxIndex].id)}
                >
                  <Heart className="h-5 w-5 mr-2" />
                  {photos[lightboxIndex].like_count || 0}
                </Button>
                
                {photos[lightboxIndex].uploaded_by === user?.id && (
                  <Button 
                    variant="ghost" 
                    className="text-red-400 hover:bg-red-400/10"
                    onClick={() => {
                      handleDeletePhoto(photos[lightboxIndex].id);
                      setLightboxOpen(false);
                    }}
                  >
                    Delete
                  </Button>
                )}
                
                <span className="text-white/60 text-sm">
                  {lightboxIndex + 1} / {photos.length}
                </span>
              </div>
            </div>
            
            <Button 
              size="icon" 
              variant="ghost" 
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/10"
              onClick={(e) => { e.stopPropagation(); navigateLightbox(1); }}
              disabled={lightboxIndex === photos.length - 1}
            >
              <ChevronRight className="h-8 w-8" />
            </Button>
          </div>
        )}
      </div>
    </ClientLayout>
  );
};

export default AttendeeGallery;
